/* tslint:disable */
import { URI } from '@vscode/base-node/vs/base/common/uri';
import * as types from '@vscode/base-node/vs/base/common/types';
import * as objects from '@vscode/base-node/vs/base/common/objects';
import { IConfigurationOverrides, ConfigurationTarget, IConfigurationModel, IConfigurationRegistry, IOverrides } from '../interfaces';
import { Registry } from '@workbench-stack/platform';
import { OVERRIDE_PROPERTY_PATTERN, Extensions } from '../constants';

export function isConfigurationOverrides(thing: any): thing is IConfigurationOverrides {
    return thing
        && typeof thing === 'object'
        && (!thing.overrideIdentifier || typeof thing.overrideIdentifier === 'string')
        && (!thing.resource || thing.resource instanceof URI);
}

export function ConfigurationTargetToString(configurationTarget: ConfigurationTarget) {
    switch (configurationTarget) {
        case ConfigurationTarget.USER: return 'USER';
        case ConfigurationTarget.WORKSPACE: return 'WORKSPACE';
        case ConfigurationTarget.WORKSPACE_FOLDER: return 'WORKSPACE_FOLDER';
        case ConfigurationTarget.DEFAULT: return 'DEFAULT';
        case ConfigurationTarget.MEMORY: return 'MEMORY';
    }
}
/**
 * A helper function to get the configuration value with a specific settings path (e.g. config.name.setting)
 */
export function getConfigurationValue<T>(config: any, settingPath: string, defaultValue?: T): T {
    function accessSetting(config: any, path: string[]): any {
        let current = config;
        for (let i = 0; i < path.length; i++) {
            if (typeof current !== 'object' || current === null) {
                return undefined;
            }
            current = current[path[i]];
        }
        return <T>current;
    }

    const path = settingPath.split('.');
    const result = accessSetting(config, path);

    return typeof result === 'undefined' ? defaultValue : result;
}


export function merge(base: any, add: any, overwrite: boolean): void {
    Object.keys(add).forEach(key => {
        if (key in base) {
            if (types.isObject(base[key]) && types.isObject(add[key])) {
                merge(base[key], add[key], overwrite);
            } else if (overwrite) {
                base[key] = add[key];
            }
        } else {
            base[key] = add[key];
        }
    });
}

export function getConfigurationKeys(): string[] {
    const properties = Registry.Instance.as<IConfigurationRegistry>(Extensions.Configuration).getConfigurationProperties();
    return Object.keys(properties);
}

export function getDefaultValues(): any {
    const valueTreeRoot: any = Object.create(null);
    const properties = Registry.Instance.as<IConfigurationRegistry>(Extensions.Configuration).getConfigurationProperties();

    for (let key in properties) {
        let value = properties[key].default;
        addToValueTree(valueTreeRoot, key, value, message => console.error(`Conflict in default settings: ${message}`));
    }

    return valueTreeRoot;
}


export function overrideIdentifierFromKey(key: string): string {
    return key.substring(1, key.length - 1);
}

export function keyFromOverrideIdentifier(overrideIdentifier: string): string {
    return `[${overrideIdentifier}]`;
}

export function compare(from: IConfigurationModel, to: IConfigurationModel): { added: string[], removed: string[], updated: string[] } {
    const added = to.keys.filter(key => from.keys.indexOf(key) === -1);
    const removed = from.keys.filter(key => to.keys.indexOf(key) === -1);
    const updated: string[] = [];

    for (const key of from.keys) {
        const value1 = getConfigurationValue(from.contents, key);
        const value2 = getConfigurationValue(to.contents, key);
        if (!objects.equals(value1, value2)) {
            updated.push(key);
        }
    }

    return { added, removed, updated };
}

export function toOverrides(raw: any, conflictReporter: (message: string) => void): IOverrides[] {
    const overrides: IOverrides[] = [];
    const configurationProperties = Registry.Instance.as<IConfigurationRegistry>(Extensions.Configuration).getConfigurationProperties();
    for (const key of Object.keys(raw)) {
        if (OVERRIDE_PROPERTY_PATTERN.test(key)) {
            const overrideRaw = {};
            for (const keyInOverrideRaw in raw[key]) {
                if (configurationProperties[keyInOverrideRaw] && configurationProperties[keyInOverrideRaw].overridable) {
                    overrideRaw[keyInOverrideRaw] = raw[key][keyInOverrideRaw];
                }
            }
            overrides.push({
                identifiers: [overrideIdentifierFromKey(key).trim()],
                contents: toValuesTree(overrideRaw, conflictReporter)
            });
        }
    }
    return overrides;
}

export function toValuesTree(properties: { [qualifiedKey: string]: any }, conflictReporter: (message: string) => void): any {
    const root = Object.create(null);

    for (let key in properties) {
        addToValueTree(root, key, properties[key], conflictReporter);
    }

    return root;
}

export function addToValueTree(settingsTreeRoot: any, key: string, value: any, conflictReporter: (message: string) => void): void {
    const segments = key.split('.');
    const last = segments.pop()!;

    let curr = settingsTreeRoot;
    for (let i = 0; i < segments.length; i++) {
        let s = segments[i];
        let obj = curr[s];
        switch (typeof obj) {
            case 'undefined':
                obj = curr[s] = Object.create(null);
                break;
            case 'object':
                break;
            default:
                conflictReporter(`Ignoring ${key} as ${segments.slice(0, i + 1).join('.')} is ${JSON.stringify(obj)}`);
                return;
        }
        curr = obj;
    }

    if (typeof curr === 'object') {
        curr[last] = value; // workaround https://github.com/Microsoft/vscode/issues/13606
    } else {
        conflictReporter(`Ignoring ${key} as ${segments.join('.')} is ${JSON.stringify(curr)}`);
    }
}

export function removeFromValueTree(valueTree: any, key: string): void {
    const segments = key.split('.');
    doRemoveFromValueTree(valueTree, segments);
}

function doRemoveFromValueTree(valueTree: any, segments: string[]): void {
    const first = segments.shift()!;
    if (segments.length === 0) {
        // Reached last segment
        delete valueTree[first];
        return;
    }

    if (Object.keys(valueTree).indexOf(first) !== -1) {
        const value = valueTree[first];
        if (typeof value === 'object' && !Array.isArray(value)) {
            doRemoveFromValueTree(value, segments);
            if (Object.keys(value).length === 0) {
                delete valueTree[first];
            }
        }
    }
}


